import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import categoryApi from "./categoryApi";
import productApi from "./productApi";
import designApi from "./designApi";
import purityApi from "./purityApi";

// -------- helpers to match filters in cache against an item ----------
const str = (x) => (x == null ? "" : String(x));
const sameId = (a, b) => String(a || "") === String(b || "");

// try to read item counter id from possible shapes
const getItemCounterId = (item) =>
  item?.counterId ??
  item?.CounterId ??
  item?.counter?._id ??
  item?.counter?.id ??
  item?.Counter?._id ??
  item?.Counter?.id ??
  "";

const matchesFilters = (item, args = {}) => {
  const {
    search = "",
    status = "",
    vendorId = "",
    branchId = "",
    counterId = "", // ✅ NEW
    categoryId = "",
    productId = "",
    designId = "",
    purityId = "",
    dateFrom = "",
    dateTo = "",
  } = args || {};

  // status
  if (status && str(item.status) !== str(status)) return false;

  // ids
  if (vendorId && !sameId(item.vendorId, vendorId)) return false;
  if (branchId && !sameId(item.branchId, branchId)) return false;

  // ✅ counter match
  if (counterId && !sameId(getItemCounterId(item), counterId)) return false;

  if (categoryId && !sameId(item.categoryId, categoryId)) return false;
  if (productId && !sameId(item.productId, productId)) return false;
  if (designId && !sameId(item.designId, designId)) return false;
  if (purityId && !sameId(item.purityId, purityId)) return false;

  // dates
  if (dateFrom && new Date(item.createdAt) < new Date(dateFrom)) return false;
  if (dateTo && new Date(item.createdAt) > new Date(dateTo)) return false;

  // search
  if (search) {
    const q = search.toLowerCase();
    const hay = [
      item.itemCode,
      item.sampleName,
      item.description,
      item.vendorName,
      item.branchName,
      item.categoryName,
      item.productName,
      item.designName,
      item.purityName,
    ]
      .join(" ")
      .toLowerCase();
    if (!hay.includes(q)) return false;
  }

  return true;
};

// patch list helper
const addItemsIntoListDraft = (draft, args, newItems = []) => {
  if (!Array.isArray(draft?.data)) return;
  if (args.page && Number(args.page) !== 1) {
    incrementTotals(draft, newItems.length);
    return;
  }
  draft.data.unshift(...newItems);
  if (args.limit) {
    draft.data.splice(Number(args.limit));
  }
  incrementTotals(draft, newItems.length);
};

const incrementTotals = (draft, by = 1) => {
  if (draft?.pagination?.totalDocs != null) {
    draft.pagination.totalDocs += by;
    draft.pagination.totalPages = Math.max(
      1,
      Math.ceil(
        draft.pagination.totalDocs /
          (draft?.pagination?.limit || draft?.limit || 10)
      )
    );
  } else if (draft?.total != null) {
    draft.total += by;
    draft.pages = Math.max(
      1,
      Math.ceil(draft.total / (draft?.limit || draft?.pagination?.limit || 10))
    );
  }
};

const decrementTotals = (draft, by = 1) => {
  if (draft?.pagination?.totalDocs != null) {
    draft.pagination.totalDocs = Math.max(0, draft.pagination.totalDocs - by);
    draft.pagination.totalPages = Math.max(
      1,
      Math.ceil(
        draft.pagination.totalDocs /
          (draft?.pagination?.limit || draft?.limit || 10)
      )
    );
  } else if (draft?.total != null) {
    draft.total = Math.max(0, draft.total - by);
    draft.pages = Math.max(
      1,
      Math.ceil(draft.total / (draft?.limit || draft?.pagination?.limit || 10))
    );
  }
};

const invalidateMasterCaches = (dispatch) => {
  dispatch(categoryApi.util.invalidateTags(["Category"]));
  dispatch(productApi.util.invalidateTags(["Product"]));
  dispatch(designApi.util.invalidateTags(["Design"]));
  dispatch(purityApi.util.invalidateTags(["Purity"]));
};

const stockApi = createApi({
  reducerPath: "stockApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    credentials: "include",
  }),
  tagTypes: ["LabelledItem"],
  endpoints: (builder) => ({
    addLabelledStock: builder.mutation({
      query: (payload) => ({
        url: "/labelled-items/create",
        method: "POST",
        body: payload,
      }),
      async onQueryStarted(payload, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: res } = await queryFulfilled;
          const created = Array.isArray(res?.data) ? res.data : [];
          if (!created.length) return;

          const allArgs = stockApi.util.selectCachedArgsForQuery(
            getState(),
            "getAllLabelledStock"
          );
          for (const args of allArgs) {
            const addable = created.filter((it) => matchesFilters(it, args));
            if (!addable.length) continue;

            dispatch(
              stockApi.util.updateQueryData(
                "getAllLabelledStock",
                args,
                (draft) => {
                  addItemsIntoListDraft(draft, args, addable);
                }
              )
            );
          }

          invalidateMasterCaches(dispatch)
        } catch {
          // ignore
        }
      },
    }),

    getAllLabelledStock: builder.query({
      query: (args = {}) => {
        const {
          page = 1,
          limit = 10,
          search = "",
          status = "",
          vendorId = "",
          branchId = "",
          counterId = "", // ✅ NEW
          categoryId = "",
          productId = "",
          designId = "",
          purityId = "",
          dateFrom = "",
          dateTo = "",
          sortBy,
          sortOrder,
        } = args;

        const params = { page, limit, q: search };
        if (status) params.status = status;
        if (vendorId) params.vendor = vendorId;
        if (branchId) params.branch = branchId;

        // ✅ counter param
        if (counterId) params.counter = counterId;

        if (categoryId) params.category = categoryId;
        if (productId) params.product = productId;
        if (designId) params.design = designId;
        if (purityId) params.purity = purityId;
        if (dateFrom) params.dateFrom = dateFrom;
        if (dateTo) params.dateTo = dateTo;
        if (sortBy) params.sortBy = sortBy;
        if (sortOrder) params.sortOrder = sortOrder;

        return { url: "/labelled-items", method: "GET", params };
      },
      providesTags: ["LabelledItem"],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const a = queryArgs || {};
        return [
          endpointName,
          a.page,
          a.limit,
          a.search,
          a.status,
          a.vendorId,
          a.branchId,
          a.counterId, // ✅ IMPORTANT (cache key)
          a.categoryId,
          a.productId,
          a.designId,
          a.purityId,
          a.dateFrom,
          a.dateTo,
          a.sortBy,
          a.sortOrder,
        ].join("|");
      },
      keepUnusedDataFor: 60,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    }),

    getLabelledItemById: builder.query({
      query: (id) => `/labelled-items/${id}`,
      providesTags: (_res, _err, id) => [{ type: "LabelledItem", id }],
    }),

    updateLabelledItem: builder.mutation({
      query: (formData) => {
    const id = formData.get("id");
    return {
      url: `/labelled-items/${id}`,
      method: "PUT",
      body: formData, // ✅ FormData directly
    };
  },
     async onQueryStarted(formData, { dispatch, queryFulfilled, getState }) {
  const id = formData.get("id");
  const patch = JSON.parse(formData.get("data") || "{}");

  const patches = [];
  const allArgs =
    stockApi.util.selectCachedArgsForQuery(
      getState(),
      "getAllLabelledStock"
    );

  for (const args of allArgs) {
    const p = dispatch(
      stockApi.util.updateQueryData("getAllLabelledStock", args, (draft) => {
        if (!Array.isArray(draft?.data)) return;
        const idx = draft.data.findIndex(
          (it) => String(it._id) === String(id)
        );
        if (idx >= 0) {
          draft.data[idx] = { ...draft.data[idx], ...patch };
        }
      })
    );
    patches.push(p);
  }

  const p2 = dispatch(
    stockApi.util.updateQueryData("getLabelledItemById", id, (draft) => {
      if (!draft?.data) return;
      Object.assign(draft.data, patch);
    })
  );
  patches.push(p2);

  try {
    const { data: res } = await queryFulfilled;
    const updated = res?.data;

    if (updated) {
      for (const args of allArgs) {
        dispatch(
          stockApi.util.updateQueryData("getAllLabelledStock", args, (draft) => {
            if (!Array.isArray(draft?.data)) return;
            const idx = draft.data.findIndex(
              (it) => String(it._id) === String(id)
            );
            if (idx >= 0) {
              draft.data[idx] = { ...draft.data[idx], ...updated };
            }
          })
        );
      }

      dispatch(
        stockApi.util.updateQueryData("getLabelledItemById", id, (draft) => {
          if (draft?.data) Object.assign(draft.data, updated);
        })
      );
    }
  } catch {
    patches.forEach((p) => p?.undo && p.undo());
  }
},

    }),

    deleteLabelledItem: builder.mutation({
      query: (id) => ({
        url: `/labelled-items/${id}`,
        method: "DELETE",
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const patches = [];
        const allArgs = stockApi.util.selectCachedArgsForQuery(getState(), "getAllLabelledStock");
        for (const args of allArgs) {
          const p = dispatch(
            stockApi.util.updateQueryData("getAllLabelledStock", args, (draft) => {
              if (!Array.isArray(draft?.data)) return;
              const before = draft.data.length;
              draft.data = draft.data.filter((it) => String(it._id) !== String(id));
              const removed = before - draft.data.length;
              if (removed > 0) decrementTotals(draft, removed);
            })
          );
          patches.push(p);
        }
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
    }),

    bulkDeleteLabelled: builder.mutation({
      query: (ids) => ({
        url: `/labelled-items/bulk/delete`,
        method: "POST",
        body: { ids },
      }),
      async onQueryStarted(ids, { dispatch, queryFulfilled, getState }) {
        const patches = [];
        const allArgs = stockApi.util.selectCachedArgsForQuery(getState(), "getAllLabelledStock");
        for (const args of allArgs) {
          patches.push(
            dispatch(
              stockApi.util.updateQueryData("getAllLabelledStock", args, (draft) => {
                if (!Array.isArray(draft?.data)) return;
                const before = draft.data.length;
                draft.data = draft.data.filter((it) => !ids.includes(String(it._id)));
                const removed = before - draft.data.length;
                if (removed > 0) decrementTotals(draft, removed);
              })
            )
          );
        }
        try { await queryFulfilled; } catch { patches.forEach((p) => p?.undo && p.undo()); }
      },
    }),

   bulkUploadLabelledStockExcel: builder.mutation({
  query: ({ file, branchId, counterId, vendorId }) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("branchId", branchId);
    formData.append("counterId", counterId);
    formData.append("vendorId", vendorId);

    return {
      url: "/label-bulk/upload-excel",
      method: "POST",
      body: formData,
    };
  },
}),

bulkUploadLabelledStockImages: builder.mutation({
  query: (zipFile) => {
    const formData = new FormData();
    formData.append("file", zipFile);

    return {
      url: "/label-bulk/upload-images",
      method: "POST",
      body: formData,
    };
  },
}),

    getExpectedStockForVerification: builder.mutation({
      query: (payload = {}) => ({
        url: "/labelled-items/expected-stock",
        method: "POST",
        body: payload,
      }),
    }),

    getExpectedStockForVerificationSyncPage: builder.mutation({
  query: (payload = {}) => ({
    url: "/labelled-items/expected-stock-sync-page",
    method: "POST",
    body: payload,
  }),
}),

    visualSearchLabelledItems: builder.mutation({
  query: ({ image, categoryId = "", productId = "", designId = "", purityId = "", limit = 20 }) => {
    const formData = new FormData();

    if (image) formData.append("image", image);
    if (categoryId) formData.append("categoryId", categoryId);
    if (productId) formData.append("productId", productId);
    if (designId) formData.append("designId", designId);
    if (purityId) formData.append("purityId", purityId);
    formData.append("limit", String(limit));

    return {
      url: "/labelled-items/visual-search",
      method: "POST",
      body: formData,
    };
  },
}),


  }),
});

export const {
  useAddLabelledStockMutation,
  useGetAllLabelledStockQuery,
  useGetExpectedStockForVerificationMutation,
  useGetExpectedStockForVerificationSyncPageMutation,
  useGetLabelledItemByIdQuery,
  useUpdateLabelledItemMutation,
  useDeleteLabelledItemMutation,
  useBulkDeleteLabelledMutation,
   useBulkUploadLabelledStockExcelMutation,
  useBulkUploadLabelledStockImagesMutation,
  useVisualSearchLabelledItemsMutation

} = stockApi;

export default stockApi;
